import { Box, Tooltip, Grid2 as Grid } from "@mui/material";
import React from "react";
import moment from "moment";
import { AntSwitch, DatePicker, HighlightSelectBox, SectionPaper, TextField } from "../../../components";
import { SelectBoxOption } from "../../../interfaces";
import { RevisionInfo, UserFields } from "../../../redux/slices/quote";

type FieldType = "text" | "select" | "email" | "tel" | "datepicker";

type ComponentType = {
    title: string,
    key: keyof RevisionInfo,
    type: FieldType,
    value?: string | UserFields,
    isEnabled: boolean,
    error?: boolean,
    errorMessage?: string,
    sizes: { xs: number, sm: number, md: number, lg: number, xl: number }
};

export const QuoteRevisionTable: React.FC<{
    revision_info: RevisionInfo,
    onItemChanged: (key: keyof RevisionInfo, value: UserFields | string | null) => void,
    userOptions: SelectBoxOption[],
    readOnly?: boolean
}> = (props) => {

    const {
        revision_info, onItemChanged,
        userOptions, readOnly = false
    } = props;

    const revisionStack: ComponentType[] = [
        {
            title: "Created By",
            key: "created_by",
            type: "select",
            value: revision_info.created_by,
            isEnabled: revision_info.created_by.include || false,
            error: revision_info.created_by.include && !revision_info.created_by?.value,
            errorMessage: "Please select the user",
            sizes: { xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }
        },
        {
            title: "Reviewed By",
            key: "referred_by",
            type: "select",
            value: revision_info.referred_by,
            isEnabled: revision_info.referred_by.include || false,
            error: revision_info.referred_by.include && !revision_info.referred_by?.value,
            errorMessage: "Please select the user",
            sizes: { xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }
        },
        {
            title: "Approved By",
            key: "approved_by",
            type: "select",
            value: revision_info.approved_by,
            isEnabled: revision_info.approved_by.include || false,
            error: revision_info.approved_by.include && !revision_info.approved_by?.value,
            errorMessage: "Please select the user",
            sizes: { xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }
        },
        {
            title: "Description",
            key: "description",
            type: "text",
            value: revision_info.description,
            isEnabled: true,
            error: false,
            errorMessage: "",
            sizes: { xs: 12, sm: 8, md: 8, lg: 8, xl: 8 }
        },
        {
            title: "Date",
            key: "date",
            type: "datepicker",
            value: revision_info.date,
            isEnabled: true,
            error: false,
            errorMessage: "",
            sizes: { xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }
        },
    ];

    const getField = (stack: ComponentType) => {
        switch (stack.type) {
            case "text":
            case "email":
            case "tel":
                return <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={stack.value}
                    type={stack.type}
                    label={stack.title}
                    placeholder={`Enter ${stack.title}`}
                    error={stack.error}
                    helperText={stack.errorMessage}
                    onChange={(e) => onItemChanged(stack.key, e.target.value)}
                    viewOnly={readOnly}
                />
            case "select":
                return <HighlightSelectBox
                    margin="none"
                    fullWdith
                    options={userOptions}
                    size="small"
                    disableClearable={true}
                    label={stack.title}
                    value={(stack.value as UserFields)?.value}
                    onChange={(option: SelectBoxOption) => {
                        onItemChanged(stack.key, { value: option, include: (stack.value as UserFields)?.include || false })
                    }}
                    error={stack.error}
                    helperText={stack.error ? stack.errorMessage : ""}
                    viewOnly={readOnly}
                />
            case "datepicker":
                return <DatePicker
                    size="small"
                    label={stack.title}
                    formatDensity="spacious"
                    value={stack.value && typeof stack.value === 'string' ? moment(stack.value) : null}
                    onChange={(date) => onItemChanged(stack.key, date?.toString() || null)}
                    viewOnly={readOnly}
                    disabled={readOnly} />
            default:
                return <></>
        }
    }

    return (
        <SectionPaper title={'Revision Table'}>
            <Grid container spacing={2}>
                {revisionStack.map((stack) => <Grid size={stack.sizes}
                    direction={"row"}
                    spacing={1}
                    key={stack.key}
                    sx={{
                        alignItems: "center",
                        position: "relative"
                    }}>

                    {getField(stack)}

                    {stack.type === "select" && (
                        <Box sx={{ position: "absolute", right: "40px", top: '12px' }}>
                            <Tooltip title={stack.isEnabled ? "Disable to hide in the Quote Preview" : "Enable to show in the Quote Preview"} arrow placement="top">
                                <AntSwitch
                                    checked={stack.isEnabled}
                                    onChange={(e, checked) =>
                                        onItemChanged(stack.key, { value: (stack.value as UserFields)?.value || null, include: checked })
                                    }
                                    disabled={readOnly}
                                />
                            </Tooltip>
                        </Box>
                    )}
                </Grid>)}
            </Grid>
        </SectionPaper >
    );
}